import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Plus, X, FileText, Syringe, Save, Trash2 } from "lucide-react";

export default function VetPrescribe() {
  const { petId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [meds, setMeds] = useState([]);
  const [diagnosis, setDiagnosis] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [vac, setVac] = useState({ name: "", date: new Date().toISOString().slice(0,10), next_due: "", batch_number: "", manufacturer: "", notes: "" });
  const [showVac, setShowVac] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/partner/vet/patients/${petId}`).then(r => setPatient(r.data));
    axios.get(`${API}/partner/vet/medicines`).then(r => setMeds(r.data.medicines));
  }, [petId]);

  const addMed = (medicine) => {
    setItems(prev => [...prev, {
      medicine_name: medicine.name,
      strength: medicine.strength || "",
      dose: medicine.default_dose || "",
      frequency: "",
      duration: "",
      notes: medicine.notes || "",
    }]);
  };
  const addBlank = () => setItems(prev => [...prev, { medicine_name: "", strength: "", dose: "", frequency: "", duration: "", notes: "" }]);
  const updateItem = (i, key, val) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!diagnosis.trim()) return alert("Diagnosis is required.");
    if (items.length === 0) return alert("Add at least one medicine.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/partner/vet/prescriptions`, { pet_id: petId, diagnosis, notes, items });
      navigate(`/app/prescription/${petId}/${r.data.prescription_id}`);
    } finally { setBusy(false); }
  };

  const submitVac = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/partner/vet/vaccinations`, { pet_id: petId, ...vac });
    setShowVac(false);
    setVac({ name: "", date: new Date().toISOString().slice(0,10), next_due: "", batch_number: "", manufacturer: "", notes: "" });
    axios.get(`${API}/partner/vet/patients/${petId}`).then(r => setPatient(r.data));
  };

  if (!patient) return <div className="text-ink-soft">Loading…</div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="vet-prescribe-page">
      {/* Patient header */}
      <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft flex items-center gap-4">
        <img src={patient.pet.photo} alt={patient.pet.name} className="w-20 h-20 rounded-2xl object-cover"/>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-extrabold">{patient.pet.name}</h1>
          <div className="text-sm text-ink-soft">{patient.pet.breed} · {patient.pet.age_years}y · {patient.pet.species}</div>
          <div className="text-xs text-ink-soft mt-1">Owner: {patient.owner?.name || "—"} {patient.owner?.email && <>· {patient.owner.email}</>}</div>
        </div>
        <Link to="/app/vet/patients" className="text-sm font-bold text-brand">← All patients</Link>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
        {/* Prescription builder */}
        <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft space-y-4" data-testid="rx-form">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-brand"/> New prescription</h2>
          <F label="Diagnosis / chief complaint">
            <textarea data-testid="rx-diagnosis" required rows={2} value={diagnosis} onChange={e => setDiagnosis(e.target.value)} className="li resize-none"/>
          </F>
          <F label="Clinical notes (optional)">
            <textarea data-testid="rx-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="li resize-none"/>
          </F>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-ink-soft">Medicines</div>
              <button onClick={addBlank} className="text-xs font-bold text-brand inline-flex items-center gap-1"><Plus className="w-3 h-3"/>Add blank</button>
            </div>
            {items.length === 0 && <div className="bg-surface-muted rounded-2xl p-3 text-sm text-ink-soft text-center">Pick from your catalogue on the right →</div>}
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="bg-surface-muted rounded-2xl p-3" data-testid={`rx-item-${i}`}>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <input value={it.medicine_name} onChange={e => updateItem(i, "medicine_name", e.target.value)} placeholder="Name" className="li-sm col-span-2"/>
                    <input value={it.strength} onChange={e => updateItem(i, "strength", e.target.value)} placeholder="Strength" className="li-sm"/>
                    <input value={it.dose} onChange={e => updateItem(i, "dose", e.target.value)} placeholder="Dose" className="li-sm"/>
                    <input value={it.frequency} onChange={e => updateItem(i, "frequency", e.target.value)} placeholder="Frequency" className="li-sm"/>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                    <input value={it.duration} onChange={e => updateItem(i, "duration", e.target.value)} placeholder="Duration" className="li-sm"/>
                    <input value={it.notes} onChange={e => updateItem(i, "notes", e.target.value)} placeholder="Notes" className="li-sm col-span-3"/>
                    <button onClick={() => removeItem(i)} className="text-danger text-xs font-bold inline-flex items-center gap-1 justify-end"><Trash2 className="w-3 h-3"/>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button data-testid="rx-submit" disabled={busy} onClick={submit} className="w-full bg-brand text-white rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-brand-hover disabled:opacity-60">
            <Save className="w-4 h-4"/> {busy ? "Saving…" : "Save & generate prescription"}
          </button>
          <button data-testid="record-vac-button" onClick={() => setShowVac(true)} className="w-full bg-sky/15 text-sky rounded-full py-2.5 font-bold inline-flex items-center justify-center gap-2 hover:bg-sky/25">
            <Syringe className="w-4 h-4"/> Record a vaccination
          </button>
        </section>

        {/* Medicine picker */}
        <aside className="bg-surface border border-line rounded-3xl shadow-soft overflow-hidden flex flex-col" style={{ maxHeight: "75vh" }}>
          <div className="p-4 border-b border-line flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold">Catalogue</h3>
            <Link to="/app/vet/medicines" className="text-xs font-bold text-brand">Manage →</Link>
          </div>
          <div className="overflow-y-auto p-3 space-y-1.5">
            {meds.length === 0 && <div className="text-ink-soft text-sm text-center">Add medicines first in <Link to="/app/vet/medicines" className="text-brand font-bold">Medicines</Link>.</div>}
            {meds.map(m => (
              <button key={m.medicine_id} data-testid={`pick-med-${m.medicine_id}`} onClick={() => addMed(m)} className="w-full text-left bg-surface-muted hover:bg-brand/10 rounded-xl p-2.5 transition-colors">
                <div className="font-bold text-sm">{m.name} <span className="text-ink-soft font-normal text-xs">{m.strength}</span></div>
                <div className="text-xs text-ink-soft">{m.type} · {m.default_dose || "no default dose"}</div>
              </button>
            ))}
          </div>
        </aside>
      </div>

      {/* Existing records */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
          <h3 className="font-heading text-lg font-bold mb-2">Past prescriptions ({patient.prescriptions?.length || 0})</h3>
          <div className="space-y-2">
            {(patient.prescriptions || []).slice(0, 5).map(rx => (
              <Link key={rx.prescription_id} to={`/app/prescription/${patient.pet.pet_id}/${rx.prescription_id}`} className="block bg-surface-muted rounded-2xl px-3 py-2 text-sm hover:bg-brand/10">
                <div className="font-bold">{rx.diagnosis}</div>
                <div className="text-xs text-ink-soft">{new Date(rx.created_at).toLocaleDateString()} · {rx.items?.length} med(s)</div>
              </Link>
            ))}
            {!patient.prescriptions?.length && <div className="text-ink-soft text-sm">None yet.</div>}
          </div>
        </div>
        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
          <h3 className="font-heading text-lg font-bold mb-2">Vaccinations ({patient.vaccinations?.length || 0})</h3>
          <div className="space-y-2">
            {(patient.vaccinations || []).slice(0, 5).map(v => (
              <div key={v.record_id} className="bg-surface-muted rounded-2xl px-3 py-2 text-sm">
                <div className="font-bold">{v.name}</div>
                <div className="text-xs text-ink-soft">{v.date} · next due {v.next_due || "—"}</div>
              </div>
            ))}
            {!patient.vaccinations?.length && <div className="text-ink-soft text-sm">None yet.</div>}
          </div>
        </div>
      </section>

      {showVac && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setShowVac(false)}>
          <form onSubmit={submitVac} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="vac-form">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl font-extrabold">Record vaccination</h3>
              <button type="button" onClick={() => setShowVac(false)} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
            </div>
            <div className="space-y-3">
              <F label="Vaccine name"><input required value={vac.name} onChange={e=>setVac({...vac, name:e.target.value})} className="li" placeholder="Rabies / DHPP / FVRCP" data-testid="vac-name"/></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Date administered"><input required type="date" value={vac.date} onChange={e=>setVac({...vac, date:e.target.value})} className="li" data-testid="vac-date"/></F>
                <F label="Next due"><input type="date" value={vac.next_due} onChange={e=>setVac({...vac, next_due:e.target.value})} className="li"/></F>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Batch #"><input value={vac.batch_number} onChange={e=>setVac({...vac, batch_number:e.target.value})} className="li"/></F>
                <F label="Manufacturer"><input value={vac.manufacturer} onChange={e=>setVac({...vac, manufacturer:e.target.value})} className="li"/></F>
              </div>
              <F label="Notes"><input value={vac.notes} onChange={e=>setVac({...vac, notes:e.target.value})} className="li"/></F>
            </div>
            <button data-testid="vac-save" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold">Save vaccination</button>
          </form>
        </div>
      )}

      <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:1.5rem;padding:0.6rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.li:focus{border-color:#FF6B35;}.li-sm{border:1px solid #EAE0D5;border-radius:0.75rem;padding:0.4rem 0.7rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;font-size:13px;}.li-sm:focus{border-color:#FF6B35;}`}</style>
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
